import { Platform, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { useState, type ReactNode } from 'react';

import { Text } from '@/components/ui';
import { useTheme } from '@/theme';

export type Column<T> = {
  key: keyof T | string;
  label: string;
  width?: number;
  render?: (row: T) => ReactNode;
};

export function DataTable<T extends { id?: string }>({
  columns,
  rows,
  onRowPress,
  emptyText = 'Keine Daten',
}: {
  columns: Column<T>[];
  rows: T[];
  onRowPress?: (row: T) => void;
  emptyText?: string;
}) {
  const theme = useTheme();
  const totalWidth = columns.reduce((acc, c) => acc + (c.width ?? 160), 0);

  return (
    <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ minWidth: '100%' }}>
      <View style={{ minWidth: totalWidth, flex: 1 }}>
        <View
          style={[
            styles.headerRow,
            { borderBottomColor: theme.colors.rule, backgroundColor: theme.colors.bgSoft },
          ]}
        >
          {columns.map((c) => (
            <View key={String(c.key)} style={{ width: c.width ?? 160, paddingHorizontal: 16 }}>
              <Text variant="eyebrow" tone="fgMute">{c.label}</Text>
            </View>
          ))}
        </View>
        {rows.length === 0 ? (
          <View style={styles.empty}>
            <Text variant="body" tone="fgMute">{emptyText}</Text>
          </View>
        ) : (
          rows.map((r, i) => <DataRow key={(r.id as string) ?? i} row={r} columns={columns} onPress={onRowPress} />)
        )}
      </View>
    </ScrollView>
  );
}

function DataRow<T>({
  row,
  columns,
  onPress,
}: {
  row: T;
  columns: Column<T>[];
  onPress?: (row: T) => void;
}) {
  const theme = useTheme();
  const [hovered, setHovered] = useState(false);
  return (
    <Pressable
      accessibilityRole={onPress ? 'button' : undefined}
      onPress={onPress ? () => onPress(row) : undefined}
      {...(Platform.OS === 'web' ? { onHoverIn: () => setHovered(true), onHoverOut: () => setHovered(false) } : {})}
      style={[
        styles.row,
        { borderBottomColor: theme.colors.rule, backgroundColor: hovered ? theme.colors.bgSoft : 'transparent' },
      ]}
    >
      {columns.map((c, j) => {
        const value = c.render ? c.render(row) : ((row as any)[c.key as keyof T] as unknown as ReactNode);
        return (
          <View key={String(c.key) + j} style={{ width: c.width ?? 160, paddingHorizontal: 16, paddingVertical: 12 }}>
            {typeof value === 'string' || typeof value === 'number' ? (
              <Text variant="body" numberOfLines={1}>{value}</Text>
            ) : (
              value ?? null
            )}
          </View>
        );
      })}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  headerRow: {
    flexDirection: 'row',
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    borderBottomWidth: 1,
  },
  empty: {
    padding: 32,
    alignItems: 'center',
  },
});
